import { useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

interface GarageInfoTabProps {
  garage: {
    name: string;
    managerName: string;
    phone: string;
    email: string;
    dailyCapacity: number;
    numberOfWorkers: number;
    city: string;
    area: string;
    streetAddress: string;
    latitude: number | null;
    longitude: number | null;
  };
  onUpdateEmail?: (email: string) => void;
}

export default function GarageInfoTab({ garage, onUpdateEmail }: GarageInfoTabProps) {
  const { t } = useLanguage();
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(garage.email || '');

  const handleSaveEmail = () => {
    if (onUpdateEmail && emailValue.trim()) {
      onUpdateEmail(emailValue.trim());
    }
    setEditingEmail(false);
  };

  return (
    <div className="profile-tab-content">
      <section className="profile-section">
        <h3 className="profile-section-title">{t('profile.info.basicInfo')}</h3>
        <div className="profile-info-grid">
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.garageName')}</span>
            <span className="profile-info-value">{garage.name || '-'}</span>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.managerName')}</span>
            <span className="profile-info-value">{garage.managerName || '-'}</span>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.phone')}</span>
            <span className="profile-info-value">{garage.phone || '-'}</span>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.email')}</span>
            {editingEmail ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  style={{ padding: '0.375rem 0.5rem', border: '1px solid rgba(3,12,84,0.12)', borderRadius: '6px', fontSize: '0.875rem' }}
                />
                <button onClick={handleSaveEmail} className="dialog-btn-primary" style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}>
                  {t('common.save')}
                </button>
                <button onClick={() => setEditingEmail(false)} className="dialog-btn-secondary" style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}>
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <span className="profile-info-value">
                {garage.email || '-'}
                {onUpdateEmail && (
                  <button onClick={() => { setEmailValue(garage.email || ''); setEditingEmail(true); }} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '0.8125rem', textDecoration: 'underline' }}>
                    {t('common.edit')}
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.carCapacity')}</span>
            <span className="profile-info-value">{garage.dailyCapacity != null ? garage.dailyCapacity : '-'}</span>
          </div>
          {/* Size field removed — only Car Capacity is used */}
        </div>
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">{t('profile.info.location')}</h3>
        <div className="profile-info-grid">
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.city')}</span>
            <span className="profile-info-value">{garage.city || '-'}</span>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.area')}</span>
            <span className="profile-info-value">{garage.area || '-'}</span>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label">{t('profile.info.streetAddress')}</span>
            <span className="profile-info-value">{garage.streetAddress || '-'}</span>
          </div>
        </div>

        {garage.latitude && garage.longitude && (
          <div className="profile-map-container">
            <iframe
              title="Garage Location"
              width="100%"
              height="300"
              style={{ border: 0, borderRadius: '8px' }}
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${garage.latitude},${garage.longitude}&zoom=15`}
            />
          </div>
        )}
      </section>

    </div>
  );
}
